import { Injectable } from '@angular/core';
import { filter, map, Observable, Subject } from 'rxjs';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';

@Injectable({ providedIn: 'root' })
export class WebSocketsService {
  /* Websockets */
  private _sockets: Record<string, WebSocket> = {};

  /* Mensajes Recibidos */
  private _mensajes$ = new Subject<{ id: string; contenido: any }>();

  constructor() {}

  /**
   * Crea un websocket con el id y la url especificados.
   * @param {string} id - El id del websocket a crear.
   * @param {string} url - La url del websocket a crear.
   * @returns {Promise<void>} - Una promesa que se resuelve cuando el websocket se abre (promise).
   * @author Juan Corral
   */
  public async crearSocket$(id: string, url: string): Promise<void> {
    if (this._sockets[id]) return;

    // Crear el websocket
    this._sockets[id] = new WebSocket(url);

    // Escuchar mensajes del websocket
    this._sockets[id].onmessage = (evento: MessageEvent) => {
      if (!evento.data) return;
      const datos = JSON.parse(evento.data);
      this._mensajes$.next({ id, contenido: datos.message });
    };

    // Re-conectar el websocket si se cierra
    this._sockets[id].onclose = (ev: CloseEvent) => {
      delete this._sockets[id];
      setTimeout(() => this.crearSocket$(id, url), 5000);
    };

    // Devolver una promesa que se resuelve cuando el websocket se abre
    return new Promise(
      (resolve) => (this._sockets[id]!.onopen = () => resolve()),
    );
  }

  /**
   * Cierra el websocket con el id especificado.
   * @param {string} id - El id del websocket a cerrar.
   * @throws {ErrorFrontend} - Si no existe un websocket con el id especificado.
   * @author Juan Corral
   */
  public cerrarSocket(id: string): void {
    if (!this._sockets[id]) return;
    this._sockets[id].onclose = () => {};
    this._sockets[id].close();
    delete this._sockets[id];
  }

  /**
   * Envía un mensaje al websocket con el id especificado.
   * @param {string} id - El id del websocket al que enviar el mensaje.
   * @param {any} datos - Los datos a enviar al websocket.
   * @author Juan Corral
   */
  public enviarMensaje(id: string, datos: any): void {
    if (!this._sockets[id])
      throw new ErrorFrontend(`No existe un socket con id ${id}`);
    const mensaje = JSON.stringify({ message: datos });
    this._sockets[id].send(mensaje);
  }

  /**
   * Escucha los mensajes del websocket con el id especificado.
   * @param {string} id - El id del websocket del que escuchar los mensajes.
   * @returns {Observable<string>} - Un observable que emite los mensajes del websocket (observable).
   * @autor Juan Corral
   */
  public escucharMensajes$(id: string): Observable<any> {
    if (!this._sockets[id])
      throw new ErrorFrontend(`No existe un socket con id ${id}`);
    return this._mensajes$.asObservable().pipe(
      filter((mensaje) => mensaje.id === id),
      map((mensaje) => mensaje.contenido),
    );
  }
}
